import { createHash } from 'node:crypto';
import { z } from 'zod';
import {
  Id,
  type Publication,
  type PublicationState,
  type Reel,
  type Render,
  type RenderQuality,
  type Script,
} from './domain.js';
import { PlaybookRef } from './learning.js';

/**
 * Production contracts from the technical review of 2026-11-01.
 *
 * One ProductionManifest names exactly what a render is made of. Render, quality assessment and publish
 * intent all point to it. A later edit creates a new manifest; it never changes the meaning of an old one.
 */

// ---------- digests ----------

/** Stable JSON: object keys sorted, so equal content gives equal digests. */
export function canonicalJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  if (value && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => a.localeCompare(b));
    return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${canonicalJson(v)}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

export function sha256(value: string | Uint8Array): string {
  return createHash('sha256').update(value).digest('hex');
}

/** Digest of the script content that matters for production (version excluded). */
export function scriptDigest(script: Pick<Script, 'lines' | 'title' | 'end' | 'theme' | 'music'>): string {
  return sha256(
    canonicalJson({
      title: script.title,
      lines: script.lines,
      end: script.end,
      theme: script.theme,
      music: script.music,
    }),
  );
}

export function captionDigest(caption: string | undefined): string {
  return sha256((caption ?? '').normalize('NFC').trim());
}

// ---------- manifest ----------

export const ProductionManifest = z.object({
  id: Id,
  orgId: Id,
  reelId: Id,
  /** Reel generation at claim time; fencing token together with runId. */
  generation: z.number().int().nonnegative(),
  runId: z.string(),
  sourceDigest: z.string(),
  script: z.object({ version: z.number().int().nonnegative(), digest: z.string() }),
  take: z
    .object({
      n: z.number().int().positive(),
      key: z.string(),
      etag: z.string().optional(),
      versionId: z.string().optional(),
    })
    .optional(),
  /** AI voice instead of a take. */
  voice: z.object({ provider: z.string(), voiceId: z.string() }).optional(),
  assets: z.array(z.object({ key: z.string(), etag: z.string().optional() })).default([]),
  playbook: PlaybookRef.optional(),
  /** Model per step and prompt bundle version actually used. */
  models: z.record(z.string(), z.string()).default({}),
  promptVersion: z.string().default(''),
  /** Renderer release: serve URL plus content digest of the Remotion site. */
  renderer: z.object({ serveUrl: z.string(), digest: z.string() }).optional(),
  /** Code release the pipeline ran on (git SHA). */
  release: z.string().default(''),
  createdBy: z.string(),
  createdAt: z.string(),
});
export type ProductionManifest = z.infer<typeof ProductionManifest>;

/** Builds a manifest from the reel's current approved state. Pure apart from the given id. */
export function buildManifest(input: {
  id: string;
  reel: Reel;
  script: Script;
  runId: string;
  generation: number;
  sourceText: string;
  createdBy: string;
  now: string;
  renderer?: ProductionManifest['renderer'];
  release?: string;
  promptVersion?: string;
}): ProductionManifest {
  const take = input.reel.takes.find((t) => t.n === input.reel.selectedTake);
  return ProductionManifest.parse({
    id: input.id,
    orgId: input.reel.orgId,
    reelId: input.reel.id,
    generation: input.generation,
    runId: input.runId,
    sourceDigest: sha256(input.sourceText),
    script: { version: input.script.version, digest: scriptDigest(input.script) },
    ...(take
      ? {
          take: {
            n: take.n,
            key: take.key,
            ...(take.etag ? { etag: take.etag } : {}),
            ...(take.versionId ? { versionId: take.versionId } : {}),
          },
        }
      : {}),
    assets: input.reel.assets.map((a) => ({ key: a.key })),
    ...(input.reel.playbook ? { playbook: input.reel.playbook } : {}),
    models: {},
    promptVersion: input.promptVersion ?? '',
    ...(input.renderer ? { renderer: input.renderer } : {}),
    release: input.release ?? '',
    createdBy: input.createdBy,
    createdAt: input.now,
  });
}

// ---------- publication eligibility (review F04) ----------

export type IneligibleReason =
  | 'no_render'
  | 'render_stale'
  | 'quality_pending'
  | 'quality_unchecked'
  | 'quality_failed_facts'
  | 'quality_failed_video'
  | 'assessment_mismatch'
  | 'no_caption'
  | 'not_connected'
  | 'publication_open';

export interface Eligibility {
  ok: boolean;
  reasons: Array<{ code: IneligibleReason; message: string }>;
}

const OPEN_STATES: PublicationState[] = ['intent', 'submitting', 'unknown', 'accepted'];

export function isPublicationOpen(p: Publication | undefined): boolean {
  return !!p && OPEN_STATES.includes(p.state);
}

/**
 * Whether a reel may be published now. Every missing, pending, failed, stale or mismatched assessment
 * blocks. Factual failures can never be overridden; video warnings only with a recorded staff override.
 */
export function publicationEligibility(input: {
  reel: Pick<Reel, 'render' | 'selectedTake' | 'caption' | 'publication'>;
  scriptVersion: number;
  scriptDigest: string;
  manifest?: Pick<ProductionManifest, 'id' | 'script'>;
  connected: boolean;
}): Eligibility {
  const reasons: Eligibility['reasons'] = [];
  const add = (code: IneligibleReason, message: string) => reasons.push({ code, message });
  const r: Render | undefined = input.reel.render;
  if (!input.connected) add('not_connected', 'Connect Instagram first.');
  if (isPublicationOpen(input.reel.publication))
    add('publication_open', 'A publication for this reel is already in progress.');
  if (!input.reel.caption?.trim()) add('no_caption', 'Write a caption first.');
  if (!r) {
    add('no_render', 'Make the video first.');
    return { ok: false, reasons };
  }
  const stale =
    r.scriptVersion !== input.scriptVersion ||
    r.take !== input.reel.selectedTake ||
    (input.manifest && input.manifest.script.digest !== input.scriptDigest) ||
    (input.manifest && r.manifestId !== undefined && r.manifestId !== input.manifest.id);
  if (stale) add('render_stale', 'Script or take changed since this video. Make it again.');
  const q = r.quality;
  if (!q) add('quality_unchecked', 'This video was made before the checks existed. Make it again.');
  else if (q.status === 'pending') add('quality_pending', 'The video checks are still running.');
  else if (q.status === 'unchecked')
    add('quality_unchecked', 'The video could not be checked. Run the checks again before posting.');
  else {
    if (!r.digest || q.assessedDigest !== r.digest)
      add('assessment_mismatch', 'The checks belong to a different video file. Run them again.');
    else if (q.assessedCaptionDigest !== captionDigest(input.reel.caption))
      add('assessment_mismatch', 'The caption changed since the checks. Run them again.');
    if (q.fidelity && !q.fidelity.passed)
      add(
        'quality_failed_facts',
        `The finished video changes facts: ${q.fidelity.claimViolations.join('; ') || 'see checks'}.`,
      );
    if (q.status === 'failed' && q.fidelity?.passed !== false) {
      const hard = q.video?.hardFailures ?? [];
      if (hard.length) add('quality_failed_video', `Video check failed: ${hard.join(', ')}.`);
      else if (!q.override)
        add('quality_failed_video', `Video warnings need a review: ${(q.video?.warnings ?? []).join(', ')}.`);
    }
  }
  return { ok: reasons.length === 0, reasons };
}

/** Derives the overall quality status from its parts. Unchecked fidelity is never "passed". */
export function qualityStatus(q: {
  fidelity?: { passed: boolean; missing: string[] };
  video?: { hardFailures: string[]; warnings: string[] };
  override?: { by: string; reason: string; at: string };
}): RenderQuality['status'] {
  if (!q.fidelity || q.fidelity.missing.length > 0) return 'unchecked';
  if (!q.fidelity.passed) return 'failed';
  if (!q.video) return 'pending';
  if (q.video.hardFailures.length) return 'failed';
  if (q.video.warnings.length && !q.override) return 'failed';
  return 'passed';
}

// ---------- publication state machine ----------

const NEXT: Record<PublicationState, PublicationState[]> = {
  intent: ['submitting', 'cancelled'],
  submitting: ['accepted', 'published', 'rejected', 'unknown'],
  unknown: ['accepted', 'published', 'rejected', 'unknown'],
  accepted: ['published', 'rejected', 'unknown'],
  published: [],
  rejected: [],
  cancelled: [],
};

export function canTransition(from: PublicationState, to: PublicationState): boolean {
  return NEXT[from].includes(to);
}

// ---------- uploads (review F09) ----------

/**
 * Every upload gets a random id and an immutable key before the upload policy is issued. A take is
 * finalized only from a pending upload of the same org and reel, after the object's size and type were
 * checked; the take keeps the object's ETag.
 */
export const Upload = z.object({
  id: Id,
  orgId: Id,
  reelId: z.string().optional(),
  kind: z.enum(['asset', 'take']),
  key: z.string(),
  contentType: z.string(),
  maxBytes: z.number().int().positive(),
  status: z.enum(['pending', 'finalized', 'expired']),
  expiresAt: z.string(),
  createdBy: z.string(),
  createdAt: z.string(),
  finalizedAt: z.string().optional(),
  etag: z.string().optional(),
  versionId: z.string().optional(),
  sizeBytes: z.number().int().nonnegative().optional(),
  /** The take this upload became (set atomically with finalize). */
  takeN: z.number().int().positive().optional(),
});
export type Upload = z.infer<typeof Upload>;

export const UPLOAD_TTL_SECONDS = 15 * 60;
